import { format } from "date-fns";
import ReactDatePicker from "react-datepicker";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import styled from "styled-components";
import { isDateBeforePlanMin, normalizeCalendarDate } from "../../helpers/dateRangePlanLimits";

interface Props {
  selectedDate: { year: number; month: number; day: number };
  onDateChange: (date: Date) => void;
  onDateSelect: (date: Date) => void;
  onShowMonthView: () => void;
  pickerType: "start" | "end";
  disableDate?: Date;
  planMinDate?: Date;
  onPlanLimitInteract?: () => void;
}

const CalendarView = ({
  selectedDate,
  onDateChange,
  onDateSelect,
  onShowMonthView,
  pickerType,
  disableDate,
  planMinDate,
  onPlanLimitInteract,
}: Props) => {
  const formattedDisableDate = disableDate
    ? normalizeCalendarDate(disableDate)
    : undefined;
  const formattedPlanMinDate = planMinDate
    ? normalizeCalendarDate(planMinDate)
    : undefined;

  const isPlanLocked = (date: Date) =>
    pickerType === "start" && isDateBeforePlanMin(date, formattedPlanMinDate);

  const handleDateInteraction = (date: Date, proceed: () => void) => {
    if (isPlanLocked(date)) {
      onPlanLimitInteract?.();
      return;
    }
    proceed();
  };

  return (
    <Wrapper type={pickerType}>
      <ReactDatePicker
        selected={
          new Date(selectedDate.year, selectedDate.month, selectedDate.day)
        }
        inline
        isClearable
        onChange={(date) => date && handleDateInteraction(date, () => onDateChange(date))}
        onSelect={(date) => handleDateInteraction(date, () => onDateSelect(date))}
        calendarClassName="date-default-view"
        fixedHeight
        minDate={pickerType === "end" ? formattedDisableDate : undefined}
        maxDate={pickerType === "start" ? formattedDisableDate : undefined}
        dayClassName={(date) => (isPlanLocked(date) ? "plan-locked-day" : undefined)}
        renderDayContents={(dayOfMonth, date) => (
          <span
            className={isPlanLocked(date) ? "plan-locked-day-content" : undefined}
            onClick={
              isPlanLocked(date)
                ? (event) => {
                    event.stopPropagation();
                    onPlanLimitInteract?.();
                  }
                : undefined
            }>
            {dayOfMonth}
          </span>
        )}
        renderCustomHeader={({ date, increaseMonth, decreaseMonth }) => (
          <>
            <span
              className="cal-header nav nav-left"
              onClick={() => decreaseMonth()}>
              <FaChevronLeft fontSize={"20px"} />
            </span>
            <span className="cal-header title" onClick={onShowMonthView}>
              {format(date, "MMMM yyyy")}
            </span>
            <span
              className="cal-header nav nav-right"
              onClick={() => increaseMonth()}>
              <FaChevronRight fontSize={"20px"} />
            </span>
          </>
        )}
      />
    </Wrapper>
  );
};

export default CalendarView;

const Wrapper = styled.div`
  > div {
    width: calc(100%);
    > .date-default-view {
      border: none;
      width: calc(100%);
      background-color: ${({ theme }) => theme.widget.background};
      overflow: hidden;
      > .react-datepicker__month-container {
        background-color: ${({ theme }) => theme.widget.background};
        float: none;
        .react-datepicker__header--custom {
          padding: 10px 20px 0px 20px;
          border-bottom: 0;
          background-color: ${({ theme }) => theme.widget.background};
          color: ${({ theme }) => theme.text};
          padding-top: 19px;
          padding-left: 20px;
          padding-right: 20px;
          > .nav {
            display: inline;
            cursor: pointer;
            user-select: none;
          }
          & .nav-left {
            float: left;
          }
          & .nav-right {
            float: right;
          }
          > .cal-header.title {
            cursor: pointer;
            font-size: 20px;
            font-weight: 700;
            line-height: 26px;
            user-select: none;
          }
        }
        .react-datepicker__day-name {
          color: ${({ theme }) => theme.text};
        }
        .react-datepicker__day {
          color: ${({ theme }) => theme.text};
          font-weight: 500;
          font-size: 15px;
          line-height: 1.5rem;
          margin: 0.12rem;
          &.react-datepicker__day--disabled {
            color: grey !important;
            cursor: not-allowed !important;
            &:hover {
              background-color: transparent !important;
            }
          }
          &.plan-locked-day {
            color: #9aa5b1 !important;
            cursor: pointer !important;
            opacity: 0.65;
            position: relative;

            &:hover {
              background-color: rgba(58, 169, 141, 0.12) !important;
              opacity: 1;
            }

            .plan-locked-day-content {
              display: inline-block;
              width: 100%;
              height: 100%;
              cursor: pointer;
            }
          }
          &.react-datepicker__day--today{
            color: red;
            background-color: transparent;
            font-weight: 800;
          }
          &.react-datepicker__day--keyboard-selected{
            background: none;
          }
          &.react-datepicker__day--selected {
            color: #fff;
            background-color: #3aa98d;
            border: 1px solid #3aa98d;
            padding-left: 5px;
            padding-right: 5px;
            width: 30px;
            border-radius: ${(props) =>
              props.type === "start" ? `30px` : `30px`};
            &:hover {
              color: #fff;
              background-color: #3aa98d;
            }
          }
          &.react-datepicker__day--today {
            font-weight: 800;
          }
          &:hover {
            color: ${({ theme }) => theme.text};
            background-color: ${({ theme }) => theme.background};
          }

          &.react-datepicker__day--outside-month{
            opacity: 0.5;
            &:hover{
              opacity: 1;
            }
          }
        }
      }
    }
  }
`;
